
import { supabase } from "@/integrations/supabase/client";
import { ShoppingList } from "@/types";
import { getShoppingLists } from "./get-lists.service";
import { mapItemFromDatabase } from "../utils";
import { addShoppingItem } from "@/services/shopping/items/index";
import logger from "@/utils/logger";
import { getCachedUserId, setCachedUser, ensureAuthenticated } from "@/services/auth/sessionCache";
import type { User } from "@supabase/supabase-js";
import { validateQuantity } from "@/utils/security";

/**
 * Get the primary list for the current user or create one if it doesn't exist
 */
export const getPrimaryList = async (): Promise<ShoppingList | null> => {
  try {
    const userId = await ensureAuthenticated();
    if (!userId) {
      console.error("❌ No authenticated user available");
      return null;
    }
    setCachedUser({ id: userId } as User);

    // 1. Check if primary list already exists
    const { data: existingList, error: existingListError } = await supabase
      .from("shopping_lists")
      .select("*")
      .eq("user_id", userId)
      .eq("is_primary", true)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    if (existingListError) {
      if (existingListError.message?.toLowerCase().includes("multiple")) {
        logger.warn("Multiple primary lists found, using the first result", existingListError);
      } else {
        console.error("Error fetching existing primary list:", existingListError);
      }
    }

    if (existingList) {
      const { data: items, error: itemsError } = await supabase
        .from("shopping_items")
        .select("*")
        .eq("list_id", existingList.id)
        .order("position", { ascending: true, nullsFirst: true });

      if (itemsError) {
        console.error("Error fetching list items:", itemsError);
        return {
          id: existingList.id,
          name: existingList.name,
          createdAt: new Date(existingList.created_at),
          updatedAt: new Date(existingList.updated_at),
          isPrimary: existingList.is_primary,
          items: [],
          collaborators: [],
          user_id: userId
        };
      }

      return {
        id: existingList.id,
        name: existingList.name,
        createdAt: new Date(existingList.created_at),
        updatedAt: new Date(existingList.updated_at),
        isPrimary: existingList.is_primary,
        items: items.map(mapItemFromDatabase),
        collaborators: [],
        user_id: userId
      };
    }

    // 2. Create new primary list if none found
    const { data: newList, error: newListError } = await supabase
    .from("shopping_lists")
    .insert([{ 
      name: "Lista della spesa", 
      user_id: userId, 
      is_primary: true 
    }])
    .select()
    .maybeSingle();
  

    if (newListError || !newList) {
      console.error("❌ Failed to create primary list:", newListError);
      return null;
    }
    logger.log("✅ Created primary list:", newList);
    

    // 3. Fetch and prepare default categories
    const { data: defaultCategories, error: defaultCategoriesError } = await supabase
    .from("default_categories")
    .select("*");

    logger.log("Fetched default categories:", defaultCategories);
    if (defaultCategoriesError) console.error("Error fetching default categories:", defaultCategoriesError);
    if (!defaultCategories || defaultCategories.length === 0) {
      console.error("defaultCategories is empty!");
      return null;
    }
    
    // Crea mapping e categorie utente
    const categoryMapping: Record<string, string> = {};
    const userCategories = defaultCategories.map((c, i) => {
      const newCategoryUUID = crypto.randomUUID();
      categoryMapping[c.id] = newCategoryUUID;
    
      return {
        id: newCategoryUUID,
        user_id: userId,
        name: c.name,
        icon: c.icon,
        color: c.color,
        text_color: c.text_color,
        position: i,
      };
    });
    

    // Inserisci categorie utente
    const { data: insertedCategories, error: categoryError } = await supabase
    .from("user_categories")
    .insert(userCategories)
    .select();

    if (categoryError) {
    console.error("Error inserting user categories:", categoryError);
    }

    // 4. Fetch and insert default products
    const { data: defaultProducts, error: defaultProductsError } = await supabase
    .from("default_products")
    .select("*")
    .eq("is_active", true);

    if (defaultProductsError) {
    console.error("Error fetching default products:", defaultProductsError);
    return null;
    }

    if (defaultProducts) {
    const userProducts = defaultProducts.map((p) => {
      return {
        user_id: userId,
        name: p.name,
        default_unit: p.default_unit,
        default_quantity: p.default_quantity,
        category_id: categoryMapping[p.category_id], // ⚠️ category_id deve essere quello personalizzato
        photo: p.photo,
      };
    });

    const { error: productError } = await supabase
      .from("user_products")
      .insert(userProducts);

    if (productError) {
      console.error("❌ Error inserting user products:", productError);
    } else {
      logger.log("✅ User products inserted:", userProducts);
    }

    // Insert default 'Acqua naturale' item in the new list
    try {
      const { data: acquaProduct, error: acquaError } = await supabase
        .from("user_products")
        .select("*")
        .eq("user_id", userId)
        .eq("name", "Acqua naturale")
        .single();

      if (acquaError) {
        console.error("Error fetching 'Acqua naturale' product:", acquaError);
      } else if (acquaProduct) {
        const quantityValidation = validateQuantity(acquaProduct.default_quantity ?? 1);

        if (!quantityValidation.isValid) {
          logger.warn("Default quantity for 'Acqua naturale' non valida", acquaProduct.default_quantity);
        }

        await addShoppingItem(newList.id, {
          product_id: acquaProduct.id,
          quantity: quantityValidation.isValid ? quantityValidation.sanitized : "1",
          unit: acquaProduct.default_unit || "",
          category: acquaProduct.category_id || "",
          attributes: acquaProduct.attributes || []
        });
        logger.log("✅ Default item 'Acqua naturale' inserted");
      }
    } catch (defaultItemError) {
      console.error("Error inserting default item:", defaultItemError);
    }
    }



    // 5. Return the new list object
    return {
      id: newList.id,
      name: newList.name,
      createdAt: new Date(newList.created_at),
      updatedAt: new Date(newList.updated_at),
      isPrimary: newList.is_primary,
      items: [],
      collaborators: [],
      user_id: userId
    };

  } catch (error) {
    console.error("Error getting primary list:", error);
    return null;
  }
};

/**
 * Set a list as the primary list for the current user
 */
export const setPrimaryList = async (listId: string): Promise<boolean> => {
  try {
    let userId = await getCachedUserId();
    if (!userId) {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("No authenticated user found");
      setCachedUser(userData.user);
      userId = userData.user.id;
    }

    await supabase
      .from("shopping_lists")
      .update({ is_primary: false })
      .eq("user_id", userId)
      .eq("is_primary", true);

    const { error: updateError } = await supabase
      .from("shopping_lists")
      .update({ is_primary: true })
      .eq("id", listId)
      .eq("user_id", userId);

    if (updateError) {
      console.error("Error setting new primary list:", updateError);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error setting primary list:", error);
    return false;
  }
};
